import { i18n } from '../i18n';

/**
 * Convert UNIX timestamp to human readable month name and year
 * @returns {string} Returns as April 2023 in locale of user
 */
export const toLocaleMonthNameAndYear = (dateTime: number, monthBaseKey = ''): string => {
    const date = new Date(dateTime);
    const year = date.getFullYear();
    const monthName = localizeMonthName(date.getMonth() + 1, monthBaseKey);
    return `${monthName} ${year}`;
};

/**
 * Convert UNIX timestamp to human readable date with localization
 * @returns {string} Returns as April 20, 2023
 */
export const formatDate = (timeStamp: number, monthBaseKey = '', format: 'MDY' | 'DMY' = 'MDY'): string => {
    let timestampMillis;
    const timeStampLength = String(timeStamp).length;
    if (timeStampLength === 16) {
        // Timestamp is in microseconds, convert to milliseconds
        timestampMillis = timeStamp / 1000;
    }
    else if (timeStampLength === 10) {
        // Timestamp is in seconds, convert to milliseconds
        timestampMillis = timeStamp * 1000;
    }
    else {
        // Timestamp is in milliseconds
        timestampMillis = timeStamp;
    }

    // const date = new Date(timeStamp);
    const date = new Date(timestampMillis);

    const monthName = localizeMonthName(date.getMonth() + 1, monthBaseKey);
    // Return date format based on the format parameter
    if (format === 'DMY') {
        return date.getDate() + ' ' + monthName + ' ' + date.getFullYear();
    } else {
        return monthName + ' ' + date.getDate() + ', ' + date.getFullYear();
    }
};

/**
 * Convert UNIX timestamp to human readable date with localization
 * @returns {string} Returns as April 20, 2023
 */
export const fomatTime = (timeStamp: number): string => {
    let timestampMillis;
    const timeStampLength = String(timeStamp).length;
    if (timeStampLength === 16) {
        // Timestamp is in microseconds, convert to milliseconds
        timestampMillis = timeStamp / 1000;
    } else {
        // Timestamp is in milliseconds
        timestampMillis = timeStamp;
    }

    // const date = new Date(timeStamp);
    const date = new Date(timestampMillis);

    return date.toLocaleTimeString('en-US', {
        hour12: true,
        hour: '2-digit',
        minute: '2-digit',
    });
};

/**
 * Localize the month name
 * @returns {string} Returns as April
 */
export const localizeMonthName = (month: number, monthBaseKey = ''): string => {
    let monthNameKey = `month_${month}`;
    if (monthBaseKey.length) {
        monthNameKey = `${monthBaseKey}${month}`;
    }
    const finalMonthName = i18n.t(monthNameKey) as string;
    if (finalMonthName === monthNameKey) {
        return month.toString();
    }
    return finalMonthName;
};

/**
 * Checks if the current date is before or equal the given target date.
 * @param targetDate - A Date object or ISO date string (e.g., "2025-07-31T23:59:59Z").
 * @returns boolean - true if the current date is before or equal the target date.
 */
export const isBeforeOrEqualDate = (targetDate: number | string): boolean => {
    const now = new Date();
    if (typeof targetDate === 'string' && isNaN(Number(targetDate))) {
        const target = new Date(targetDate);
        if (!hasTimeInDateString(targetDate)) {
            target.setHours(23, 59, 59, 999);
        }
        return now <= target;
    } else {
        const target = getTimeStampInMilliSeconds(targetDate as number);
        return now <= new Date(target);
    }
};

export const isBetweenInDates = (startDate: number | string, endDate: number | string): boolean => {
    const now = new Date();
    let targetStartDate: Date;
    let targetEndDate: Date;

    if (typeof startDate === 'string' && isNaN(Number(startDate))) {
        targetStartDate = new Date(startDate);
    } else {
        targetStartDate = new Date(getTimeStampInMilliSeconds(Number(startDate)));
    }

    if (typeof endDate === 'string' && isNaN(Number(endDate))) {
        targetEndDate = new Date(endDate);
        if (!hasTimeInDateString(endDate)) {
            targetEndDate.setHours(23, 59, 59, 999);
        }
    } else {
        targetEndDate = new Date(getTimeStampInMilliSeconds(Number(endDate)));
    }

    return now >= targetStartDate && now <= targetEndDate;
};

const getTimeStampInMilliSeconds = (timeStmp: number) => {
    const tsLength = String(timeStmp).length;
    const tsNum = Number(timeStmp);
    if (tsLength === 10) return tsNum * 1000; // seconds → ms
    if (tsLength === 13) return tsNum; // already ms
    if (tsLength === 16) return Math.floor(tsNum / 1000); // microseconds → ms
    return tsNum;
};

function hasTimeInDateString(dateStr: string): boolean {
    if (isNaN(Date.parse(dateStr))) return false;
    const d = new Date(dateStr);
    return d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0 || d.getUTCSeconds() !== 0 || d.getUTCMilliseconds() !== 0;
}
